/* eslint-disable @typescript-eslint/no-unsafe-argument */
import { type Href, Redirect, router, useLocalSearchParams } from "expo-router"
import { useEffect, useRef, useState } from "react"
import { Platform } from "react-native"
import { jwtDecode } from "jwt-decode"
import { sdk } from "@/api/sdk"
import { GOOGLE_SCOPES } from "@/constants/Scopes"
import { useMutation } from "@tanstack/react-query"
import config from "@/config"
import { useRegistrationInputs } from "@/stores/register"
import { useGoogleLoginAuthMutation } from "@/queries/user"
import { type GoogleLoginAuthMutation } from "@/generated/graphql"
import { usePreQualState } from "@/stores/preQual"
import * as signInEvents from "@/analytics/events/sign-in"
import * as registerEvents from "@/analytics/events/register"
import { showErrorToast } from "@/components/toasts/AppToast"
import FullScreenLoader from "@/components/loading/FullScreenLoaderDark"
import { Sentry } from "@/hooks/sentry"

const GOOGLE_SIGN_IN_ERROR_MESSAGE =
  "Could not complete Google sign in. Please try again."

interface GoogleCodeVerificationVariables {
  code: string
  redirectUri: string
  scopes: string[]
}

let claimedGoogleCodeVerification:
  | {
      code: string
      request: ReturnType<typeof sdk.GoogleCodeVerify>
    }
  | undefined

const verifyGoogleCodeOnce = async (
  variables: GoogleCodeVerificationVariables
) => {
  if (claimedGoogleCodeVerification?.code === variables.code) {
    return await claimedGoogleCodeVerification.request
  }

  const request = sdk.GoogleCodeVerify(variables)
  claimedGoogleCodeVerification = { code: variables.code, request }
  return await request
}

const removeGoogleCodeFromBrowserUrl = () => {
  if (Platform.OS !== "web" || typeof window === "undefined") {
    return
  }

  const url = new URL(window.location.href)
  if (!url.searchParams.has("code")) {
    return
  }

  url.searchParams.delete("code")
  window.history.replaceState(
    window.history.state,
    "",
    `${url.pathname}${url.search}${url.hash}`
  )
}

const getGoogleLoginErrorMessage = (error: unknown) => {
  const errorMessage = (
    error as {
      response?: { errors?: Array<{ message?: unknown }> }
    }
  )?.response?.errors?.[0]?.message

  if (errorMessage === "GOOGLE_LOGIN_FAILED") {
    return "Google sign in failed. Please try again."
  }

  if (errorMessage === "GOOGLE_NO_USER_FOUND") {
    return "User does not exist. Sign up instead."
  }

  return typeof errorMessage === "string"
    ? errorMessage
    : GOOGLE_SIGN_IN_ERROR_MESSAGE
}

interface GoogleIdToken {
  iss: string
  azp: string
  aud: string
  sub: string
  email: string
  email_verified: boolean
  nbf: number
  name?: string
  picture?: string
  given_name?: string
  family_name?: string
  iat: number
  exp: number
  jti: string
}

export default function GoogleCallbackScreen() {
  const { code } = useLocalSearchParams<{
    code?: string
  }>()

  const [authState, setAuthState] = useState<
    "initial" | "verifying" | "failed" | "complete"
  >("initial")
  const [redirectTo, setRedirectTo] = useState<Href>("/login")
  const claimedCodeRef = useRef<string | null>(null)
  const verificationSucceededRef = useRef(false)

  const googleLoginAuthMutation = useGoogleLoginAuthMutation()
  const preQualState = usePreQualState()
  const isPreQualFlow = !!preQualState?.propertiesFlipped

  const {
    setFirstName,
    setLastName,
    setConfirmationMode,
    setGoogleCode,
    setEmail,
  } = useRegistrationInputs()

  const googleVerify = useMutation({
    mutationFn: verifyGoogleCodeOnce,
    onSuccess: (data) => {
      verificationSucceededRef.current = true
      const existingUser = data.googleVerify?.existing || false
      const idToken = data.googleVerify?.idToken ?? null
      const userProfile = idToken ? jwtDecode<GoogleIdToken>(idToken) : null
      if (existingUser && idToken) {
        googleLoginAuthMutation.mutate(
          {
            serviceType: "auth",
            idToken,
            scopes: GOOGLE_SCOPES,
            redirectUri: config.GOOGLE_REDIRECT_URI,
          },
          {
            onSuccess: (data: GoogleLoginAuthMutation) => {
              if (data?.googleLogin?.token) {
                signInEvents.googleSignInSubmitted({
                  email: userProfile?.email ?? "",
                })
                if (isPreQualFlow) {
                  setRedirectTo("/loans/")
                } else {
                  setRedirectTo("/")
                }
                setAuthState("complete")
              } else {
                showErrorToast({
                  title: "Sign In Error",
                  message: "Could not complete sign in.",
                })
                setRedirectTo("/login")
                setAuthState("failed")
              }
            },
            onError: (error) => {
              showErrorToast({
                title: "Sign In Error",
                message: getGoogleLoginErrorMessage(error),
              })
              setRedirectTo("/login")
              setAuthState("failed")
              Sentry.captureException(error)
              console.error(error)
            },
          }
        )
      } else if (idToken) {
        setFirstName(userProfile?.given_name ?? "")
        setLastName(userProfile?.family_name ?? "")
        setEmail(userProfile?.email ?? "")
        setGoogleCode({
          serverAuthCode: null,
          idToken,
          redirectUri: config.GOOGLE_REDIRECT_URI,
          scopes: GOOGLE_SCOPES,
          user: {
            id: userProfile?.sub ?? "",
            email: userProfile?.email ?? "",
            givenName: userProfile?.given_name ?? "",
            familyName: userProfile?.family_name ?? "",
            name: userProfile?.name ?? "",
            photo: userProfile?.picture ?? "",
          },
        })
        setConfirmationMode("google")
        registerEvents.googleAuthRegistrationSubmitted({
          email: userProfile?.email ?? "",
          userExists: false,
        })
        setRedirectTo("/confirm")
        setAuthState("complete")
      } else {
        showErrorToast({
          title: "Sign In Error",
          message: "Could not complete sign in.",
        })
        setRedirectTo("/register")
        setAuthState("failed")
      }
    },
    onError: (error) => {
      if (verificationSucceededRef.current) {
        return
      }

      showErrorToast({
        title: "Sign In Error",
        message: "Could not verify Google sign in. Please try again.",
      })
      setRedirectTo("/login")
      setAuthState("failed")
      Sentry.captureException(error)
      console.error(error)
    },
  })

  useEffect(() => {
    if (!code) {
      setAuthState("failed")
      return
    }

    if (claimedCodeRef.current === code) {
      return
    }

    claimedCodeRef.current = code
    removeGoogleCodeFromBrowserUrl()
    setAuthState("verifying")
    googleVerify.mutate({
      code,
      redirectUri: config.GOOGLE_REDIRECT_URI,
      scopes: GOOGLE_SCOPES,
    })
  }, [code])

  useEffect(() => {
    if (["complete", "failed"].includes(authState)) {
      console.log("!! Google redirecting to ", redirectTo)
      router.replace(redirectTo)
    }
  }, [authState])

  if (["initial", "verifying"].includes(authState)) {
    return <FullScreenLoader>Completing Sign In</FullScreenLoader>
  }

  if (["complete", "failed"].includes(authState)) {
    return <FullScreenLoader>Done</FullScreenLoader>
  }

  return <Redirect href={redirectTo} />
}
